/**
 * Audit Log Page
 * Displays admin audit logs with filtering capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    IconButton,
    Chip,
    Select,
    MenuItem,
    SelectChangeEvent,
    Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox, DatePicker } from '../../components/ui';
import { AuditLogService } from '../../services/auditLog/AuditLogService';
import { formatDateTime } from '../../utils/dateUtils';
import type { AuditLog, AuditLogFilters } from '../../types/auditLog';
import { getActionTypeDisplayName } from '../../types/auditLog';

interface DateRange {
    start: string;
    end: string;
}

const AuditLogPage: React.FC = () => {
    const theme = useTheme();
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const debouncedSearchValue = useDebounce(searchValue, 500);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalResults, setTotalResults] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    // Status filter
    const [statusFilter, setStatusFilter] = useState<string>('');

    // Date Range state
    const [dateRange, setDateRange] = useState<DateRange | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const filters: AuditLogFilters = {
                page,
                pageSize,
                actionType: debouncedSearchValue || undefined,
                actionStatus: statusFilter || undefined,
                startDate: dateRange?.start || undefined,
                endDate: dateRange?.end ? dateRange.end + 'T23:59:59' : undefined,
            };
            const result = await AuditLogService.getAuditLogs(filters);
            setLogs(result?.data || []);
            setTotalResults(result?.total || 0);
            setTotalPages(Math.ceil((result?.total || 0) / pageSize));
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
            setLogs([]);
            setTotalResults(0);
            setTotalPages(0);
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, debouncedSearchValue, statusFilter, dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        setPage(1);
    };

    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        setStatusFilter(event.target.value);
        setPage(1);
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    // Date Range handlers
    const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
        const newRange = {
            ...(dateRange || { start: '', end: '' }),
            [field]: value,
        };
        setDateRange(newRange.start || newRange.end ? newRange : null);
        setPage(1);
    };

    const handleClearDateRange = () => {
        setDateRange(null);
        setPage(1);
    };

    const toggleRowExpanded = (id: string) => {
        setExpandedRow(expandedRow === id ? null : id);
    };

    return (
        <MainLayout>


            {/* Page Header */}
            <Box sx={{ marginBottom: theme.spacing(3) }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: theme.typography.h4.fontWeight,
                        color: theme.palette.text.primary,
                        marginBottom: theme.spacing(1),
                    }}
                >
                    Audit Log
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: theme.palette.text.secondary }}
                >
                    View admin operation logs and activities across the platform.
                </Typography>
            </Box>

            {/* Search and Date Range */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: theme.spacing(3),
                    flexWrap: 'wrap',
                    gap: theme.spacing(2),
                }}
            >
                {/* Search Box */}
                <SearchBox
                    placeholder="Search by Action Type..."
                    value={searchValue}
                    onChange={handleSearch}
                    containerSx={{ width: { xs: '100%', md: 300 } }}
                />

                {/* Status Filter */}
                <Select
                    value={statusFilter}
                    onChange={handleStatusChange}
                    displayEmpty
                    size="small"
                    sx={{
                        minWidth: 120,
                        height: 40,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.customColors.tableBorder,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                        },
                    }}
                >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="Success">Success</MenuItem>
                    <MenuItem value="Failed">Failed</MenuItem>
                </Select>

                {/* Date Range Inputs */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    <DatePicker
                        value={dateRange?.start || ''}
                        onChange={(e) => handleDateRangeChange('start', e.target.value)}
                        sx={{ width: 150 }}
                    />
                    <Typography sx={{ color: theme.palette.text.secondary }}>~</Typography>
                    <DatePicker
                        value={dateRange?.end || ''}
                        onChange={(e) => handleDateRangeChange('end', e.target.value)}
                        sx={{ width: 150 }}
                    />
                    {dateRange && (
                        <IconButton
                            size="small"
                            onClick={handleClearDateRange}
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Audit Log Table */}
            <Card>
                <CardContent noPadding>
                    {isLoading ? (
                        <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
                            <LoadingSpinner />
                        </Box>
                    ) : logs.length === 0 ? (
                        <Box sx={{ padding: theme.spacing(4) }}>
                            <EmptyState
                                title="No Audit Logs Found"
                                message="There are no audit logs matching your criteria."
                            />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeaderCell sx={{ width: 50 }}></TableHeaderCell>
                                            <TableHeaderCell>Action Type</TableHeaderCell>
                                            <TableHeaderCell>Actor</TableHeaderCell>
                                            <TableHeaderCell>Role</TableHeaderCell>
                                            <TableHeaderCell>Date</TableHeaderCell>
                                            <TableHeaderCell>Status</TableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {logs.map((log) => (
                                            <React.Fragment key={log.id}>
                                                <TableRow
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => toggleRowExpanded(log.id)}
                                                >
                                                    <TableCell>
                                                        <IconButton size="small">
                                                            {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: theme.palette.text.primary,
                                                            }}
                                                        >
                                                            {getActionTypeDisplayName(log.actionType)}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                color: theme.palette.text.secondary,
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            {log.actionType}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{log.actorName}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={log.actorRole}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    log.actorRole === 'Administrator'
                                                                        ? theme.palette.primary.main
                                                                        : theme.palette.info.main,
                                                                color: theme.palette.common.white,
                                                                fontSize: '0.75rem',
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                                                        {formatDateTime(log.actionDate)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={
                                                                log.actionStatus === 'Success' ? (
                                                                    <SuccessIcon sx={{ fontSize: 16, color: 'inherit' }} />
                                                                ) : (
                                                                    <ErrorIcon sx={{ fontSize: 16, color: 'inherit' }} />
                                                                )
                                                            }
                                                            label={log.actionStatus}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    log.actionStatus === 'Success'
                                                                        ? theme.palette.success.main
                                                                        : theme.palette.error.main,
                                                                color: theme.palette.common.white,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                                {/* Expanded Details */}
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={6}
                                                        sx={{
                                                            padding: 0,
                                                            borderBottom: expandedRow === log.id ? undefined : 'none',
                                                        }}
                                                    >
                                                        <Collapse in={expandedRow === log.id}>
                                                            <Box
                                                                sx={{
                                                                    padding: theme.spacing(2),
                                                                    backgroundColor: theme.palette.action.hover,
                                                                }}
                                                            >
                                                                <Typography variant="subtitle2" sx={{ marginBottom: 1, fontWeight: 600 }}>
                                                                    Request Details:
                                                                </Typography>
                                                                <Box
                                                                    component="pre"
                                                                    sx={{
                                                                        backgroundColor: theme.palette.background.default,
                                                                        padding: theme.spacing(2),
                                                                        borderRadius: 1,
                                                                        overflow: 'auto',
                                                                        fontSize: '0.8rem',
                                                                        fontFamily: 'monospace',
                                                                        maxHeight: 300,
                                                                        margin: 0,
                                                                    }}
                                                                >
                                                                    {(() => {
                                                                        try {
                                                                            return JSON.stringify(JSON.parse(log.requestDetails), null, 2);
                                                                        } catch {
                                                                            return log.requestDetails;
                                                                        }
                                                                    })()}
                                                                </Box>
                                                                {log.errorMessage && (
                                                                    <Box sx={{ marginTop: theme.spacing(2) }}>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{
                                                                                marginBottom: 1,
                                                                                fontWeight: 600,
                                                                                color: theme.palette.error.main,
                                                                            }}
                                                                        >
                                                                            Error Message:
                                                                        </Typography>
                                                                        <Typography variant="body2" color="error">
                                                                            {log.errorMessage}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            <TablePagination
                                page={page}
                                pageSize={pageSize}
                                totalItems={totalResults}
                                onPageChange={setPage}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        </>
                    )}
                </CardContent>
            </Card>
        </MainLayout>
    );
};

export default AuditLogPage;
